import { useState, useEffect, useCallback } from "react";
import { Loader2, Crown, Zap, CheckCircle2, Receipt, Sparkles } from "lucide-react";
import { createCheckout, getCheckoutStatus, getCheckoutHistory } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { planToTier } from "@/lib/tierGate";
import { isOwner } from "@/lib/owner";

const TIERS = [
  {
    key: "plus", title: "Snaggedify Plus", subtitle: "Top 3 deal sources · 50 saves · 10 daily deck cards", icon: Zap, badge: null,
    features: ["Top 3 reseller sources shown per result (vs. 1 on Free)", "Save up to 50 items to your Vault", "10 cards in the Daily Recommendation Deck", "Exact local sales tax by your saved ZIP/State"],
    monthly: { tier: "plus", price: 4.99 }, annual: { tier: "plus_yearly", price: 49.90 },
  },
  {
    key: "pro", title: "Snaggedify Pro", subtitle: "Unlimited sources & saves · 15 daily deck cards", icon: Crown, badge: "MOST POPULAR",
    features: ["Unlimited reseller sources shown per result", "Unlimited Vault saves", "15 cards in the Daily Recommendation Deck", "Full PDP reviews (YouTube/TikTok/Reddit) unlocked", "Detailed OEM vs. Reseller price breakdown"],
    monthly: { tier: "pro", price: 9.99 }, annual: { tier: "pro_yearly", price: 99.90 },
  },
  {
    key: "gold", title: "Snaggedify Elite", subtitle: "Everything in Pro + unlimited daily deck & early alerts", icon: Crown, badge: null,
    features: ["Everything in Pro, unlimited daily deck cards", "Priority verification queue (faster Check results)", "Early deal notifications before they hit the feed"],
    monthly: { tier: "gold", price: 19.99 }, annual: { tier: "gold_yearly", price: 199.90 },
  },
];

const card = "rounded-2xl border border-gray-200 bg-white dark:border-hairline dark:bg-charcoal-surface shadow-sm";

const PLAN_LABEL = { free: "Free", plus: "Plus", pro: "Pro", gold: "Elite" };

export default function ShopTab() {
  const { checkAuth, user } = useAuth();
  const effectiveTier = isOwner(user) ? "ELITE" : planToTier(user?.plan);
  const KEY_TO_TIER = { plus: "PLUS", pro: "PRO", gold: "ELITE" };
  const [loadingTier, setLoadingTier] = useState(null);
  const [error, setError] = useState("");
  const [payment, setPayment] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [billing, setBilling] = useState("monthly");

  const loadPurchases = useCallback(async () => {
    try {
      const data = await getCheckoutHistory();
      setPurchases(data.purchases || []);
    } catch {
      setPurchases([]);
    }
  }, []);

  useEffect(() => { loadPurchases(); }, [loadPurchases]);

  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 6) {
      setPayment({ state: "failed", message: "Status check timed out. If charged, your credits will arrive shortly." });
      return;
    }
    try {
      const data = await getCheckoutStatus(sessionId);
      if (data.payment_status === "paid") {
        setPayment({ state: "success", tier: data.tier });
        toast.success("🎉 You're upgraded! Enjoy Snaggedify.", { duration: 6000 });
        checkAuth();
        loadPurchases();
        return;
      }
      if (data.status === "expired") {
        setPayment({ state: "failed", message: "Payment session expired. Please try again." });
        return;
      }
      setPayment({ state: "checking" });
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      setPayment({ state: "failed", message: e.message || "Could not verify payment." });
    }
  }, [checkAuth, loadPurchases]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const cancelled = params.get("checkout") === "cancelled";
    if (sessionId) {
      setPayment({ state: "checking" });
      pollStatus(sessionId);
    } else if (cancelled) {
      setError("Checkout was cancelled. No charge was made.");
    }
    if (sessionId || cancelled) {
      window.history.replaceState({}, "", "/app");
    }
  }, [pollStatus]);

  const handleCheckout = async (tierId) => {
    setLoadingTier(tierId);
    setError("");
    try {
      const data = await createCheckout({ tier: tierId });
      if (data?.url) window.location.href = data.url;
      else setError("Checkout session did not return a URL.");
    } catch (e) {
      setError(e.message || "Checkout failed.");
    } finally {
      setLoadingTier(null);
    }
  };

  return (
    <div className="text-gray-900 dark:text-white" data-testid="shop-tab">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-orange">Snaggedify · {PLAN_LABEL[user?.plan] || "Free"}</p>
      <h2 className="mt-2 text-3xl md:text-4xl font-bold tracking-tighter text-gray-900 dark:text-[#F3F4F6]">Choose your plan</h2>
      <p className="mt-3 text-sm text-gray-500 dark:text-zinc-400 max-w-md">Unlock more reseller sources per deal, bigger Daily Decks, and a bigger Vault to save your finds. Pick a monthly plan or save with yearly.</p>
      <p className="mt-2 text-xs font-bold uppercase tracking-[0.1em] text-red-500" data-testid="refund-disclaimer">All sales are final. No refunds.</p>

      {payment?.state === "checking" && (
        <div className="mt-6 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="checkout-checking">
          <Loader2 className="h-5 w-5 animate-spin text-brand-orange" />
          <p className="text-sm font-bold uppercase tracking-[0.1em] text-amber-700">Verifying your payment…</p>
        </div>
      )}
      {payment?.state === "success" && (
        <div className="mt-6 flex items-center gap-3 rounded-2xl border border-green-200 bg-green-50 p-4" data-testid="checkout-success">
          <CheckCircle2 className="h-5 w-5 text-green-500" />
          <p className="text-sm font-bold uppercase tracking-[0.1em] text-green-700">Payment successful — your account has been upgraded!</p>
        </div>
      )}      {payment?.state === "failed" && (
        <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="checkout-failed">
          <p className="text-sm text-red-600">{payment.message}</p>
        </div>
      )}
      {error && (
        <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="checkout-error">
          <p className="text-xs font-bold uppercase tracking-[0.1em] text-red-500">Checkout Error</p>
          <pre className="mt-2 whitespace-pre-wrap text-sm text-red-600">{error}</pre>
        </div>
      )}

      {/* Billing period toggle */}
      <div className="mt-6 flex justify-center">
        <div className="inline-flex items-center rounded-full border border-gray-200 bg-white p-1 dark:border-hairline dark:bg-charcoal-surface" data-testid="billing-toggle">
          {[["monthly", "Monthly"], ["annual", "Annually"]].map(([id, label]) => (
            <button key={id} data-testid={`billing-${id}`} onClick={() => setBilling(id)} className={`rounded-full px-5 py-2 text-sm font-black transition-colors ${billing === id ? "bg-gradient-to-r from-brand-yellow to-brand-orange text-black" : "text-gray-500 dark:text-zinc-400"}`}>
              {label}{id === "annual" && <span className="ml-1.5 rounded-full bg-[#151B26] px-1.5 py-0.5 text-[9px] font-black uppercase text-white">2 months free</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Base plan grid — Free Demo + the three Slip tiers */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Free Demo card */}
        <div data-testid="plan-card-free" className="relative flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-transform hover:-translate-y-1 dark:border-hairline dark:bg-charcoal-surface">
          <Sparkles className="h-6 w-6 text-zinc-400" strokeWidth={2.5} />
          <h3 className="mt-4 text-xl font-bold tracking-tight text-gray-900 dark:text-[#F3F4F6]">Free Demo</h3>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="text-4xl font-black tracking-tighter text-gray-900 dark:text-white" data-testid="plan-price-free">$0</span>
            <span className="text-sm text-gray-400 dark:text-zinc-500">/mo</span>
          </div>
          <p className="mt-1.5 text-xs text-gray-500 dark:text-zinc-400">Try Snaggedify — no card needed</p>
          <ul className="mt-3 space-y-1.5" data-testid="plan-features-free">
            {["1 reseller source shown per result (Scroll & Discover)", "5 cards in the Daily Recommendation Deck", "Save up to 5 items to your Vault", "Unlimited free Check-a-Link verification"].map((f) => (
              <li key={f} className="flex gap-2 text-xs leading-relaxed text-gray-700 dark:text-zinc-300">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-savings" /><span>{f}</span>
              </li>
            ))}
          </ul>
          {effectiveTier === "FREE" && (
            <div className="mt-6 flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 text-sm font-black uppercase tracking-[0.08em] text-gray-400 dark:border-hairline dark:text-zinc-500" data-testid="free-current-plan">Current Plan</div>
          )}
        </div>

        {TIERS.map((p) => {
          const Icon = p.icon;
          const active = billing === "annual" ? p.annual : p.monthly;
          const busy = loadingTier === active.tier;
          const perMonth = billing === "annual" ? p.annual.price / 12 : p.monthly.price;
          const savePerYear = Math.round(p.monthly.price * 12 - p.annual.price);
          const discountPct = Math.round((1 - (p.annual.price / 12) / p.monthly.price) * 100);
          return (
            <div
              key={p.key}
              data-testid={`plan-card-${p.key}`}
              className={`relative flex flex-col rounded-2xl border bg-white dark:bg-charcoal-surface p-6 shadow-sm transition-transform hover:-translate-y-1 ${
                p.badge ? "border-brand-orange ring-1 ring-brand-orange/30" : "border-gray-200 dark:border-hairline"
              }`}
            >
              {p.badge && (
                <span data-testid={`plan-badge-${p.key}`} className="absolute -top-3 left-6 rounded-full bg-gradient-to-r from-brand-yellow to-brand-orange px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-black">{p.badge}</span>
              )}
              {billing === "annual" && (
                <span data-testid={`discount-badge-${p.key}`} className="absolute -top-3 right-4 rounded-full bg-danger px-2.5 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] text-white shadow-lg">-{discountPct}% OFF</span>
              )}
              <Icon className="h-6 w-6 text-brand-orange" strokeWidth={2.5} />
              <h3 className="mt-4 text-xl font-bold tracking-tight text-gray-900 dark:text-[#F3F4F6]">{p.title}</h3>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-4xl font-black tracking-tighter text-gray-900 dark:text-white" data-testid={`plan-price-${p.key}`}>${perMonth.toFixed(2)}</span>
                <span className="text-sm text-gray-400 dark:text-zinc-500">/mo</span>
              </div>
              {billing === "annual" ? (
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-gray-500 dark:text-zinc-400">billed ${p.annual.price.toFixed(2)}/yr</span>
                  <span data-testid={`plan-save-${p.key}`} className="rounded-full bg-savings/10 px-2 py-0.5 text-[10px] font-black uppercase text-savings">Save ${savePerYear}/yr</span>
                </div>
              ) : (
                <p className="mt-1.5 text-xs text-gray-500 dark:text-zinc-400">{p.subtitle}</p>
              )}
              <ul className="mt-3 space-y-1.5" data-testid={`plan-features-${p.key}`}>
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2 text-xs leading-relaxed text-gray-700 dark:text-zinc-300">
                    <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-savings" /><span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleCheckout(active.tier)}
                disabled={busy || KEY_TO_TIER[p.key] === effectiveTier}
                data-testid={`buy-btn-${p.key}`}
                className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-brand-yellow text-sm font-black uppercase tracking-[0.08em] text-black transition-transform hover:scale-[1.02] active:scale-100 disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {KEY_TO_TIER[p.key] === effectiveTier ? "Current Plan" : (busy ? "Redirecting…" : `Get ${p.title}`)}
              </button>
            </div>
          );
        })}
      </div>

      <div className={`${card} mt-8 overflow-hidden`} data-testid="purchase-history">
        <div className="flex items-center gap-2 border-b border-gray-200 dark:border-white/[0.08] px-6 py-4">
          <Receipt className="h-4 w-4 text-brand-orange" />
          <h3 className="text-sm font-bold uppercase tracking-[0.1em] text-gray-900 dark:text-[#F3F4F6]">Purchase History</h3>
        </div>
        {purchases.length === 0 ? (
          <p className="px-6 py-8 text-sm text-gray-400 dark:text-zinc-500" data-testid="purchase-history-empty">
            No purchases yet. Your receipts will appear here after checkout.
          </p>
        ) : (
          <ul>
            {purchases.map((p) => (
              <li key={p.session_id} data-testid={`purchase-row-${p.tier}`} className="flex items-center justify-between gap-4 border-b border-gray-200 dark:border-white/[0.08] px-6 py-4 last:border-b-0">
                <div className="min-w-0">
                  <p className="text-sm font-bold tracking-tight truncate text-gray-900 dark:text-zinc-100">{p.label}</p>
                  <p className="text-xs text-gray-400 dark:text-zinc-500">{new Date(p.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</p>
                </div>
                <span className="shrink-0 text-base font-bold tracking-tighter text-brand-orange">${Number(p.amount).toFixed(2)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
